import {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {isAxiosError} from 'axios';
import {
    adjustDecisionJobPosting,
    applyJobPosting,
    cancelJobPosting,
    confirmEndJobPosting,
    getJobPostingDetail,
    getJobPostingSettings, requestEndJobPosting
} from "../../../../api/jobApi";
import {defaultAlert} from "../../../../utils/alert";
import {cancelReservation} from "../../../../api/transaction/payApi";


export const useJobDetail = () => {
    const {jobPostingCode} = useParams<{ jobPostingCode: string }>();
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [cancelInfo, setCancelInfo] = useState<any>(null);
    const [settings, setSettings] = useState<any>(null);

    const fetch = useCallback(async () => {
        try {
            const [detailRes, settingsRes] = await Promise.all([
                getJobPostingDetail(jobPostingCode || ''),
                getJobPostingSettings(),
            ]);
            if (detailRes.data?.data) setData(detailRes.data.data);
            if (settingsRes.data) setSettings(settingsRes.data);
        } catch {
            defaultAlert('공고 정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, [jobPostingCode]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    const loadCancelInfo = async () => {
        if (!data) return;
        try {
            const {data: settings} = await getJobPostingSettings();
            const serviceStart = new Date(data.careStartDate);
            serviceStart.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const days = Math.floor((serviceStart.getTime() - today.getTime()) / 86400000);

            const [penaltyRate, cancelStandard] =
                days >= 3 ? [settings.penalty3daysBefore, '서비스 3일전'] :
                    days === 2 ? [settings.penalty2daysBefore, '서비스 2일전'] :
                        days === 1 ? [settings.penalty1dayBefore, '서비스 1일전'] :
                            [settings.penaltySameDay, '서비스 시작일 당일'];

            let dailyPay = data.additionalPay != null ? data.additionalPay : data.dailyPay;
            let paymentAmount = dailyPay * data.totalDays;
            if (data.workType === 'HOURLY' && data.workStartTime && data.workEndTime) {
                const [sH, sM] = data.workStartTime.split(':').map(Number);
                const [eH, eM] = data.workEndTime.split(':').map(Number);
                const hoursPerDay = (eH * 60 + eM - (sH * 60 + sM)) / 60;
                paymentAmount = dailyPay * hoursPerDay * data.totalDays;
            }
            const penaltyAmount = Math.floor(paymentAmount * penaltyRate / 100);

            setCancelInfo({
                cancelStandard, penaltyRate, penaltyAmount, paymentAmount,
                refundAmount: paymentAmount - penaltyAmount,
                feeRates: [
                    {description: '서비스 3일전', rate: settings.penalty3daysBefore},
                    {description: '서비스 2일전', rate: settings.penalty2daysBefore},
                    {description: '서비스 1일전', rate: settings.penalty1dayBefore},
                    // {description: '서비스 시작일 당일', rate: settings.penaltySameDay},
                ],
            });
        } catch {
            defaultAlert('취소 정보를 불러오는데 실패했습니다.');
        }
    };

    // ── 액션 핸들러 ──────────────────────────────
    const handleCancel = async () => {
        try {
            await cancelJobPosting(jobPostingCode || '');
            defaultAlert('공고가 취소되었습니다.');
        } catch (e) {
            if (isAxiosError(e)) {
                const code = e.response?.data?.code;
                defaultAlert(
                    code === 'NOT_RECRUITING' ? '모집 중인 공고가 아닙니다.' :
                        code === 'NOT_FOUND' ? '해당 공고를 찾을 수 없습니다.' :
                            e.response?.data?.message || '공고 취소에 실패했습니다.'
                );
            }
        }
    };

    const handleCancelReservation = async () => {
        try {
            await cancelReservation(jobPostingCode || '');
            defaultAlert('예약이 취소되었습니다.');
            sessionStorage.removeItem('coordinatorCode');
            fetch();
        } catch (e: any) {
            defaultAlert(e.response?.data?.message || '예약 취소에 실패했습니다.');
        }
    };

    const handleApply = async (proposedSalary: string) => {
        try {
            await applyJobPosting({jobPostingCode: jobPostingCode || '', amount: Number(proposedSalary)});
            defaultAlert('지원이 완료되었습니다.');
            fetch();
        } catch (e) {
            if (isAxiosError(e)) {
                defaultAlert(e.response?.data?.message || '지원에 실패했습니다.');
            } else {
                defaultAlert('지원에 실패했습니다.');
            }
        }
    };

    const handleConfirmEnd = async (rating: number, content: string) => {
        try {
            await confirmEndJobPosting({jobPostingCode: jobPostingCode || '', rating, content});
            defaultAlert('종료가 확정되었습니다.');
            fetch();
        } catch (e) {
            if (isAxiosError(e)) {
                const code = e.response?.data?.code;
                defaultAlert(
                    code === 'COORDINATOR_NOT_CONFIRMED' ? '코디의 종료 신청 후 확정이 가능합니다.' :
                        e.response?.data?.message || '종료 확정에 실패했습니다.'
                );
            }
        }
    };

    const handleRequestEnd = async (payOption: string, adjustDirection: string, adjustAmount: number) => {
        try {
            await requestEndJobPosting({
                jobPostingCode: jobPostingCode || '',
                payOption,
                adjustDirection,
                adjustAmount
            });
            defaultAlert('종료 신청이 완료되었습니다.');
            fetch();
        } catch (e) {
            if (isAxiosError(e)) {
                defaultAlert(e.response?.data?.message || '종료 신청에 실패했습니다.');
            }
        }
    };

    const handleAdjustDecision = async (isApproved: boolean) => {
        try {
            await adjustDecisionJobPosting(jobPostingCode || '', isApproved);
            defaultAlert(isApproved ? '급여 조정을 승인했습니다.' : '급여 조정을 거절했습니다.');
            fetch();
        } catch (e) {
            if (isAxiosError(e)) defaultAlert(e.response?.data?.message || '처리에 실패했습니다.');
        }
    };

    return {
        data, loading, cancelInfo, settings,
        loadCancelInfo,
        handleCancel, handleCancelReservation, handleApply,
        handleConfirmEnd, handleRequestEnd, handleAdjustDecision,
    };
};